/**
 * NIP-98 HTTP Auth header generation.
 *
 * Thin wrapper around nostr-tools NIP-98 implementation. Generates signed
 * authorization headers for HTTP requests that require Nostr identity
 * authentication (e.g., npub.cash API).
 *
 * SECURITY: privkey is a Uint8Array, never converted to hex or logged.
 * The function is stateless and pure.
 *
 * Tier: All tiers (infrastructure for npub.cash Cashu receive).
 *
 * @module nostr/nip98
 */

import { type EventTemplate, finalizeEvent } from 'nostr-tools/pure';
import { getToken } from 'nostr-tools/nip98';

/**
 * Create a NIP-98 authorization header for an HTTP request.
 *
 * Signs a Nostr kind:27235 event containing the request URL, method,
 * and optional payload hash. The resulting token includes the "Nostr "
 * authorization scheme prefix, ready to use as an Authorization header.
 *
 * @param url - The full URL of the HTTP request to authenticate
 * @param method - The HTTP method (GET, POST, PUT, etc.)
 * @param privkey - The user's 32-byte Nostr private key
 * @param payload - Optional request body fields for payload hash (PUT/POST)
 * @returns NIP-98 authorization token string (includes "Nostr " prefix)
 */
export async function createNip98AuthHeader(
  url: string,
  method: string,
  privkey: Uint8Array,
  payload?: Record<string, string>,
): Promise<string> {
  const sign = (event: EventTemplate) => finalizeEvent(event, privkey);

  return getToken(url, method, sign, true, payload);
}
